import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { EMPTY } from "rxjs";
import { catchError, first, tap } from "rxjs/operators";
import {
  PENDING_MFA_STORAGE_KEY,
  PendingMfaState,
} from "src/auth/pending-mfa-state";
import { AuthNewService } from "src/auth/services/auth-new.service";
import { VerifyMfaOtp_verifyMfaOtp } from "src/models/phoenix-auth/VerifyMfaOtp";

@Component({
  selector: "app-verify-otp",
  templateUrl: "./verify-otp.component.html",
  styleUrls: ["./verify-otp.component.scss"],
})
export class VerifyOtpComponent implements OnInit, AfterViewInit {
  loading = false;
  resending = false;
  errorMessage: string;
  otpId: string;
  userId: number;
  redirectUrl: string | null;
  readonly codeLength = 6;
  readonly otpIndexes = Array.from(
    { length: this.codeLength },
    (_, index) => index,
  );
  otpValues = Array.from({ length: this.codeLength }, () => "");
  maskedEmail: string | null = null;

  @ViewChildren("otpInput")
  otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  @ViewChild("verifyButton")
  verifyButton!: ElementRef<HTMLButtonElement>;

  form: UntypedFormGroup;

  constructor(
    private fb: UntypedFormBuilder,
    private auth: AuthNewService,
    private router: Router,
  ) {
    this.form = this.fb.group({
      code: [
        "",
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(6),
          Validators.pattern(/^\d{6}$/),
        ],
      ],
    });

    this.restorePendingMfaState();
  }

  get isDisabled() {
    return this.form.controls.code?.invalid;
  }

  ngOnInit(): void {
    if (!this.otpId || !this.userId) {
      this.clearPendingMfa();
      this.router.navigate(["/auth/sign-in"]);
      return;
    }

    this.writeCodeToInputs(this.form.controls.code.value || "");
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.focusDigit(0));
  }

  onDigitInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const inputEvent = event as InputEvent;
    const digits = this.sanitizeDigits(input.value);

    if (!digits) {
      this.setDigit(index, "");
      return;
    }

    if (
      inputEvent.inputType === "insertFromPaste" ||
      (index === 0 && digits.length === this.codeLength)
    ) {
      this.fillDigits(index, digits);
      return;
    }

    // Mobile keyboards can skip keydown and write directly to the input.
    this.setDigit(index, digits.slice(-1));

    this.focusAfterDigitEntry(index);
  }

  onDigitKeydown(event: KeyboardEvent, index: number) {
    if (event.key === "Backspace") {
      event.preventDefault();
      this.handleBackspace(index);
      return;
    }

    if (event.key === "Delete") {
      event.preventDefault();
      this.setDigit(index, "");
      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      this.focusDigit(index - 1);
      return;
    }

    if (event.key === "ArrowRight" && index < this.codeLength - 1) {
      event.preventDefault();
      this.focusDigit(index + 1);
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      this.focusDigit(0);
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      this.focusDigit(this.codeLength - 1);
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey || event.key === "Tab") {
      return;
    }

    if (event.key.length !== 1) {
      return;
    }

    event.preventDefault();

    if (!/\d/.test(event.key)) {
      return;
    }

    this.setDigit(index, event.key);

    this.focusAfterDigitEntry(index);
  }

  onDigitFocus(index: number) {
    const input = this.otpInputs.get(index)?.nativeElement;
    input?.select();
  }

  onPaste(event: ClipboardEvent, index: number) {
    event.preventDefault();
    const pasted = event.clipboardData?.getData("text") || "";
    this.fillDigits(index, pasted);
  }

  onSubmit() {
    if (this.form.invalid) return;

    const { code } = this.form.value;

    this.loading = true;

    this.auth
      .verifyOtp({
        otpId: this.otpId,
        userId: this.userId,
        code,
      })
      .pipe(
        first(),
        tap((auth: VerifyMfaOtp_verifyMfaOtp) => {
          this.clearPendingMfa();

          const role = auth.payload.role;
          if (this.redirectUrl) {
            this.router.navigate([this.redirectUrl], {
              state: { preventLoader: true },
            });
          } else if (role === "Admin") {
            this.router.navigate(["/app/expenses/expenses"], {
              state: { preventLoader: true },
            });
          } else if (role === "Analyst") {
            this.router.navigate(["/app/expenses/expenses"], {
              state: { preventLoader: true },
            });
          } else if (role === "User") {
            this.router.navigate(["/app/dashboard"], {
              state: { preventLoader: true },
            });
          } else {
            this.router.navigate(["/auth/sign-in"], {
              state: { preventLoader: true },
            });
          }
        }),
        catchError((e) => {
          this.errorMessage =
            e.message || "Invalid verification code. Please try again.";
          this.loading = false;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  onResend() {
    if (this.resending) return;

    this.resending = true;
    this.errorMessage = undefined;

    this.auth
      .resendOtp({
        otpId: this.otpId,
        userId: this.userId,
      })
      .pipe(
        first(),
        tap((response) => {
          this.otpId = response.otpId;
          this.userId = response.userId;
          this.writePendingMfa({
            otpId: this.otpId,
            userId: this.userId,
            redirectUrl: this.redirectUrl,
            maskedEmail: this.maskedEmail,
          });
          this.resetOtpState();
          this.resending = false;
        }),
        catchError((e) => {
          this.errorMessage = e.message || "Failed to resend code.";
          this.resending = false;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private sanitizeDigits(value: string) {
    return (value || "").replace(/\D/g, "").slice(0, this.codeLength);
  }

  onBackToLogin() {
    this.clearPendingMfa();
    this.router.navigate(["/auth/sign-in"]);
  }

  private handleBackspace(index: number) {
    if (this.otpValues[index]) {
      this.setDigit(index, "");
      return;
    }

    if (index === 0) {
      return;
    }

    this.setDigit(index - 1, "");
    this.focusDigit(index - 1);
  }

  private setDigit(index: number, value: string) {
    this.otpValues[index] = value;
    this.syncCodeControl();
  }

  private fillDigits(startIndex: number, value: string) {
    const digits = this.sanitizeDigits(value);
    if (!digits) {
      return;
    }

    let nextIndex = startIndex;
    for (const digit of digits) {
      if (nextIndex >= this.codeLength) {
        break;
      }
      this.otpValues[nextIndex] = digit;
      nextIndex++;
    }

    this.syncCodeControl();

    if (nextIndex < this.codeLength) {
      this.focusDigit(nextIndex);
      return;
    }

    this.focusDigit(this.codeLength - 1);
  }

  private writeCodeToInputs(code: string) {
    const digits = this.sanitizeDigits(code);

    for (let index = 0; index < this.codeLength; index++) {
      this.otpValues[index] = digits[index] || "";
    }

    this.syncCodeControl();
  }

  private syncCodeControl() {
    const code = this.otpValues.join("");

    if (this.form.controls.code.value !== code) {
      this.form.controls.code.setValue(code, { emitEvent: false });
    }

    this.errorMessage = undefined;
  }

  private focusAfterDigitEntry(index: number) {
    if (index < this.codeLength - 1) {
      this.focusDigit(index + 1);
      return;
    }

    if (this.otpValues.every((value) => !!value)) {
      setTimeout(() => this.focusVerifyButton());
    }
  }

  private resetOtpState() {
    this.form.reset({ code: "" });
    this.writeCodeToInputs("");
    setTimeout(() => this.focusDigit(0));
  }

  private focusVerifyButton() {
    const button = this.verifyButton?.nativeElement;
    if (button && !button.disabled) {
      button.focus();
    }
  }

  private focusDigit(index: number) {
    const targetIndex = Math.min(Math.max(index, 0), this.codeLength - 1);
    const input = this.otpInputs?.get(targetIndex)?.nativeElement;
    if (input) {
      input.focus();
      input.select();
    }
  }

  private restorePendingMfaState() {
    const nav = this.router.getCurrentNavigation();
    const state = nav?.extras?.state as Partial<PendingMfaState> | undefined;

    if (state?.otpId && typeof state.userId === "number") {
      const pendingMfaState: PendingMfaState = {
        otpId: state.otpId,
        userId: state.userId,
        redirectUrl: state.redirectUrl || null,
        maskedEmail: state.maskedEmail || null,
      };

      this.applyPendingMfaState(pendingMfaState);
      this.writePendingMfa(pendingMfaState);
      return;
    }

    const storedState = this.readPendingMfa();
    if (storedState) {
      this.applyPendingMfaState(storedState);
    }
  }

  private applyPendingMfaState(state: PendingMfaState) {
    this.otpId = state.otpId;
    this.userId = state.userId;
    this.redirectUrl = state.redirectUrl;
    this.maskedEmail = state.maskedEmail;
  }

  private readPendingMfa(): PendingMfaState | null {
    const value = sessionStorage.getItem(PENDING_MFA_STORAGE_KEY);

    if (!value) {
      return null;
    }

    try {
      const state = JSON.parse(value) as Partial<PendingMfaState>;

      if (!state?.otpId || typeof state.userId !== "number") {
        this.clearPendingMfa();
        return null;
      }

      return {
        otpId: state.otpId,
        userId: state.userId,
        redirectUrl: state.redirectUrl || null,
        maskedEmail: state.maskedEmail || null,
      };
    } catch {
      this.clearPendingMfa();
      return null;
    }
  }

  private writePendingMfa(state: PendingMfaState) {
    sessionStorage.setItem(PENDING_MFA_STORAGE_KEY, JSON.stringify(state));
  }

  private clearPendingMfa() {
    sessionStorage.removeItem(PENDING_MFA_STORAGE_KEY);
  }
}
